import { useState, useEffect, createContext, useContext, Component } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Tv, Layers, Globe, Megaphone, Settings, Languages,
  ChevronRight, ArrowLeft, LogOut, Menu, X, CreditCard,
  Users, MessageCircle, FileText, BarChart3, ScrollText,
  SlidersHorizontal, Home, Palette, AlertTriangle, RotateCcw, Inbox, ClipboardList, Send,
} from "lucide-react";
import { cn } from "@/lib/utils";
import axios from "axios";
import { API } from "@/App";

const PAGE_TITLES = {
  "/admin": "Dashboard",
  "/admin/homepage": "Homepage Builder",
  "/admin/sliders": "Hero Sliders",
  "/admin/banners": "Promotional Banners",
  "/admin/channels": "Channel Management",
  "/admin/categories": "Categories",
  "/admin/countries": "Countries",
  "/admin/languages": "Languages",
  "/admin/channel-submissions": "Channel Submissions",
  "/admin/forms": "Form Builder",
  "/admin/users": "Users & Roles",
  "/admin/audit-log": "Audit Log",
  "/admin/ads": "Ads Management",
  "/admin/membership": "Membership",
  "/admin/chat": "Chat",
  "/admin/messages": "Mass Messaging",
  "/admin/pages": "Pages",
  "/admin/analytics": "Analytics",
  "/admin/settings": "Settings",
};

const BREADCRUMB_LABELS = {
  admin: "Admin",
  homepage: "Homepage",
  sliders: "Hero Sliders",
  banners: "Banners",
  channels: "Channels",
  categories: "Categories",
  countries: "Countries",
  "channel-submissions": "Channel Submissions",
  forms: "Forms",
  submissions: "Submissions",
  edit: "Editor",
  users: "Users & Roles",
  "audit-log": "Audit Log",
  ads: "Ads",
  membership: "Membership",
  chat: "Chat",
  messages: "Mass Messaging",
  pages: "Pages",
  analytics: "Analytics",
  settings: "Settings",
};

const menuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: Home, label: "Homepage", path: "/admin/homepage" },
  { icon: SlidersHorizontal, label: "Hero Sliders", path: "/admin/sliders" },
  { icon: Palette, label: "Banners", path: "/admin/banners" },
  { icon: Tv, label: "Channels", path: "/admin/channels" },
  { icon: Layers, label: "Categories", path: "/admin/categories" },
  { icon: Globe, label: "Countries", path: "/admin/countries" },
  { icon: Languages, label: "Languages", path: "/admin/languages" },
  { icon: Inbox, label: "Channel Submissions", path: "/admin/channel-submissions", badge: "submissions" },
  { icon: ClipboardList, label: "Forms", path: "/admin/forms" },
  { icon: Users, label: "Users & Roles", path: "/admin/users" },
  { icon: ScrollText, label: "Audit Log", path: "/admin/audit-log" },
  { icon: Megaphone, label: "Ads", path: "/admin/ads" },
  { icon: CreditCard, label: "Membership", path: "/admin/membership" },
  { icon: MessageCircle, label: "Chat", path: "/admin/chat" },
  { icon: Send, label: "Mass Messaging", path: "/admin/messages" },
  { icon: FileText, label: "Pages", path: "/admin/pages" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

// Shared counter for the "New Channel Submissions" badge.
export const SubmissionsCounterContext = createContext({ counter: { new: 0, my_under_review: 0 }, refreshCounter: () => {} });
export const useSubmissionsCounter = () => useContext(SubmissionsCounterContext);

function NavBadge({ count }) {
  if (!count || count <= 0) return null;
  const label = count > 99 ? "99+" : String(count);
  return (
    <span
      data-testid="submissions-nav-badge"
      role="status"
      aria-label={`${count} new channel submissions`}
      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#FFC928] text-black text-[11px] font-bold leading-none"
    >
      {label}
    </span>
  );
}

class AdminErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Admin page error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-8" data-testid="admin-error-boundary">
          <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <AlertTriangle size={24} className="text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">Something went wrong</h3>
          <p className="text-sm text-slate-400 text-center max-w-md">
            An error occurred while loading this page. Please try again or return to the dashboard.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2"
              data-testid="error-retry-btn"
            >
              <RotateCcw size={14} /> Retry
            </button>
            <Link
              to="/admin"
              className="px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-700"
              data-testid="error-dashboard-btn"
            >
              Return to Dashboard
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Breadcrumbs({ pathname }) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length <= 1) return null;
  return (
    <nav className="flex items-center gap-1.5 text-xs text-slate-500 mb-4" data-testid="admin-breadcrumbs">
      {segments.map((seg, i) => {
        const path = "/" + segments.slice(0, i + 1).join("/");
        const label = BREADCRUMB_LABELS[seg] || seg.charAt(0).toUpperCase() + seg.slice(1);
        const isLast = i === segments.length - 1;
        return (
          <span key={path} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight size={10} className="text-slate-600" />}
            {isLast ? (
              <span className="text-slate-300 font-medium">{label}</span>
            ) : (
              <Link to={path} className="hover:text-slate-300 transition-colors">{label}</Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [counter, setCounter] = useState({ new: 0, my_under_review: 0 });

  const refreshCounter = () => {
    axios.get(`${API}/admin/channel-submissions/counter`, { withCredentials: true })
      .then((r) => setCounter(r.data))
      .catch(() => setCounter({ new: 0, my_under_review: 0 }));
  };

  // Fetch on mount and whenever the admin navigates (no polling).
  useEffect(() => { refreshCounter(); }, [location.pathname]);

  const title = PAGE_TITLES[location.pathname] || "Admin";

  const handleLogout = async () => {
    await axios.post(`${API}/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  const handleNavClick = () => setIsMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[#050814]" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-800/60 bg-[#0A0F1E]/95 backdrop-blur-xl sticky top-0 z-40">
        <h1 className="text-lg font-bold text-white truncate">{title}</h1>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h2 className="font-bold text-lg text-white">Admin Panel</h2>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-xl text-slate-400 hover:text-white transition-colors">
                <X size={22} />
              </button>
            </div>
            <nav className="flex-grow p-4 space-y-1 overflow-y-auto" data-testid="mobile-admin-nav">
              <Link to="/" onClick={handleNavClick}
                className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm font-medium mb-4 px-3 py-2.5">
                <ArrowLeft size={16} /> Back to Site
              </Link>
              {menuItems.map((item) => (
                <Link key={item.path} to={item.path} onClick={handleNavClick}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center justify-between px-4 py-3.5 rounded-xl text-sm font-medium transition-all",
                    location.pathname === item.path
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  )}>
                  <div className="flex items-center gap-3"><item.icon size={18} />{item.label}</div>
                  {item.badge === "submissions" && counter.new > 0
                    ? <NavBadge count={counter.new} />
                    : <ChevronRight size={16} />}
                </Link>
              ))}
              <button onClick={() => { handleLogout(); handleNavClick(); }}
                data-testid="mobile-logout-btn"
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all mt-4">
                <LogOut size={18} /> Logout
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Desktop: Sidebar + Main */}
      <div className="lg:grid lg:grid-cols-[260px_minmax(0,1fr)] min-h-screen">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col sticky top-0 h-screen border-r border-slate-800/60 bg-[#0A0F1E]/95 backdrop-blur-xl overflow-y-auto" data-testid="admin-sidebar">
          <div className="p-5 border-b border-slate-800/60">
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-xs font-medium mb-3">
              <ArrowLeft size={12} /> Back to Site
            </Link>
            <h2 className="font-bold text-base text-white tracking-tight">Admin Panel</h2>
          </div>
          <nav className="flex-1 p-2 space-y-0.5" data-testid="admin-nav">
            {menuItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all group",
                    active
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                      : "text-slate-400 hover:bg-slate-800/70 hover:text-white"
                  )}>
                  <div className="flex items-center gap-3"><item.icon size={16} />{item.label}</div>
                  {item.badge === "submissions" && counter.new > 0
                    ? <NavBadge count={counter.new} />
                    : <ChevronRight size={12} className={cn("transition-transform", active ? "opacity-100" : "opacity-0 group-hover:opacity-100")} />}
                </Link>
              );
            })}
          </nav>
          <div className="p-2 border-t border-slate-800/60">
            <button onClick={handleLogout} data-testid="logout-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main id="main-content" tabIndex={-1} className="min-w-0 w-full overflow-x-hidden outline-none" data-page-scroll-container>
          <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-6 max-w-[1600px]">
            <Breadcrumbs pathname={location.pathname} />
            <div className="hidden lg:block mb-6">
              <h1 className="text-2xl font-bold tracking-tight text-white" data-testid="admin-page-title">{title}</h1>
            </div>
            <AdminErrorBoundary>
              <SubmissionsCounterContext.Provider value={{ counter, refreshCounter }}>
                <Outlet />
              </SubmissionsCounterContext.Provider>
            </AdminErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
